import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useWrapRequest } from "utils";
import CartInfo from "components/Customize/CartInfo";
import { Form, Button, Col, Row, Tooltip, OverlayTrigger, Badge, Container } from "react-bootstrap";
import { getConfirmPage } from "services/get-confirmPage";
import { postConfirmPage } from "services/post-confirmPage";
import { postPromoCode } from "services/post-promoCode";
import { useRecoilState_TRANSITION_SUPPORT_UNSTABLE as useRecoilState, useRecoilValue } from "recoil";
import { cartState, agreedWithTcState, toastState, agreeWithCustomerTermsState } from "atoms";
import { gtmCheckIfExistingCustomer } from "utils/googleTagManager";

function Confirm() {
  const navigate = useNavigate();
  const wrapRequest = useWrapRequest();
  const [toast, setToast] = useRecoilState(toastState);
  const [cart, setCart] = useRecoilState(cartState);
  const agreedWithTc = useRecoilValue(agreedWithTcState);
  const agreeWithCustomerTerms = useRecoilValue(agreeWithCustomerTermsState);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [tooltip, setTooltip] = useState<string | null>(null);
  const [offerCode, setOfferCode] = useState("");
  const [offerCodeState, setOfferCodeState] = useState<boolean | null>(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      const response = await getConfirmPage();
      if (response._links?.["ec:windowLocation"]?.href) {
        return navigate(response._links["ec:windowLocation"].href);
      }
      const cartObject = response._embedded["ec:cart"];
      if (response._embedded["ec:offer-code-tooltip"]) {
        setTooltip(response._embedded["ec:offer-code-tooltip"]);
      }
      setCart(cartObject);
      if (cartObject?.promotionCode?.promoCode) {
        setOfferCode(cartObject?.promotionCode?.promoCode);
      }
      setInitialLoading(false);
      gtmCheckIfExistingCustomer(cartObject);
    };
    wrapRequest(fetchInitialData);
    // eslint-disable-next-line
  }, [navigate, setCart]);

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const onContinue = async () => {
    wrapRequest(async () => {
      const response = await postConfirmPage();
      if (response.ecommerceAction === "continue" && response._links && response._links["ec:windowLocation"]?.href) {
        navigate(response._links["ec:windowLocation"].href);
      }
    });
  };

  const postPromoCodeHandle = async () => {
    wrapRequest(async () => {
      const response = await postPromoCode(offerCode);
      if (response?.ecommerceResponse?.ecommerceAction === "continue") {
        const cart = response.ecommerceResponse?._embedded["ec:cart"];
        setCart(cart);
        if (cart?.promotionCode?.promoCode) {
          setOfferCode(cart?.promotionCode?.promoCode);
        }
        setOfferCodeState(true);
        setToast({
          ...toast,
          show: true,
          variant: "success",
          header: "success",
          message: `Offer Code ${offerCode} Applied`,
        });
      } else {
        setOfferCodeState(false);
        setToast({
          ...toast,
          show: true,
          variant: "danger",
          header: "Error",
          message: `Offer code is not valid for this order. Please check offer details. ${response?.ecommerceResponse?.message}`,
        });
      }
    });
  };

  if (initialLoading) {
    return (
      <Container className="mt-5 pt-5 mb-5" style={{ height: "30vh" }}>
        <Row className="mb-2">
          <Col className="step-title">Confirm Order</Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-5 pt-5 mb-5">
      <Row className="mb-2">
        <Col className="step-title" style={{ color: "#f2a900" }}>
          Confirm Order for {cart.customerNameFull}
        </Col>
      </Row>
      <Row>
        <Col>
          <CartInfo
            cart={cart}
            onContinue={onContinue}
            disableContinueButton={(!agreedWithTc || !agreeWithCustomerTerms) && cart.isServiceAgreementRequired}
            confirmVariation={true}
          />
          {!cart?.isCurrentCustomer && cart.promotionCode.enabled ? (
            <button className="btn btn-primary cart-registry-button" onClick={onContinue} disabled={!agreedWithTc && cart.isServiceAgreementRequired}>
              <strong>Continue</strong>
            </button>
          ) : null}
        </Col>
      </Row>
      {cart?.promotionCode?.enabled && (
        <>
          {offerCodeState !== null && (
            <Row>
              <Col className="text-end me-3 mt-1">
                {offerCodeState ? (
                  <span className="text-success">Offer Code {offerCode} Applied</span>
                ) : (
                  <span className="text-danger">Offer code is not valid for this order. Please check offer details.</span>
                )}
              </Col>
            </Row>
          )}
          <Row className="mt-2">
            <Col className={`d-flex justify-content-end me-3`}>
              {tooltip && (
                <div className="d-flex align-items-center">
                  <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
                    <Badge pill className="me-1 border-bg-pastel-light-blue" role="button" bg="primary">
                      <i className="fa-sharp fa-solid fa-info"></i>
                    </Badge>
                  </OverlayTrigger>
                </div>
              )}
              <Form.Control
                type="text"
                value={offerCode}
                placeholder="Offer code"
                className="me-2"
                style={{ maxWidth: 200 }}
                onChange={(e) => {
                  setOfferCodeState(null);
                  setOfferCode(e.target.value);
                }}
              />
              <Button
                variant="secondary"
                className="border-bg-pastel-light-blue no-radius"
                onClick={postPromoCodeHandle}
                disabled={offerCode.trim() === ""}
              >
                <strong>Apply</strong>
              </Button>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}

export default Confirm;
